import axios from 'axios';

// interface
import {
  FetchProject,
  CreateProject,
  UpdateProject,
  DeleteProject,
  FetchRoleProjectWithId,
  ListRolesProject,
  CreateRoleInProject,
  UpdateRoleInProject,
  DeleteRolesInProject,
  ManageUserInProject,
  DeleteUserInProject,
  FetchAllLockers,
  GroupWithId,
  ListGroups,
  LockerById,
  CreateGroup,
  FetchProjectWithId,
  UpdateGroup,
  DeleteGroup,
  ListDevices,
  FetchLockersByIds,
  UpdateCardGroup,
  UploadProjectPicture,
  CreateProjectTheme,
  UpdateProjectConfigModule,
  FetchAllDoors,
  UpdateProjectConfigMeetingModule,
  ListUsersProject,
} from '@app/typings/services/project';
import { endpoint, headers } from '@app/utils/service';
import { getProject, getRowsPerPage } from '@app/utils/localStorage';
import { InviteMember } from '@app/typings/services/user';
export const fetchProject: FetchProject = (brandId, name, offset, limit) => {
  return axios({
    url: endpoint(`/projects`),
    method: 'GET',
    params: {
      brandId: brandId,
      name: name,
      offset: offset || 0,
      limit: getRowsPerPage(),
    },
    ...headers(),
  });
};

export const fetchProjectWithId: FetchProjectWithId = (projectId) => {
  return axios({
    url: endpoint(`/projects/${projectId}`),
    method: 'GET',
    params: {
      showConfig: true,
    },
    ...headers(),
  });
};

export const updateProjectTheme: CreateProjectTheme = (themeData) => {
  return axios({
    url: endpoint(`/projects/${getProject()}/config`),
    method: 'POST',
    data: {
      theme: themeData,
    },
    ...headers(),
  });
};

export const updateProjectConfigModule: UpdateProjectConfigModule = (modulesVisibility) => {
  return axios({
    url: endpoint(`/projects/${getProject()}/config`),
    method: 'POST',
    data: {
      modulesVisibility,
    },
    ...headers(),
  });
};

export const updateProjectConfigMeeting: UpdateProjectConfigMeetingModule = (
  noOfBookRoomSameTime,
) => {
  return axios({
    url: endpoint(`/projects/${getProject()}/config`),
    method: 'POST',
    data: {
      noOfBookRoomSameTime,
    },
    ...headers(),
  });
};

export const createProject: CreateProject = (brandId, data) => {
  return axios({
    url: endpoint('/projects'),
    method: 'POST',
    ...headers(),
    data: {
      brandId: brandId,
      name: data.projectName,
      addresses: {
        line1: data.line1,
        line2: data.line2,
        type: 'primary',
        province: data.province,
        district: data.district,
        subdistrict: data.subdistrict,
        postalCode: data.postalCode,
        telephone1: data.officeNumber,
      },
      contact: {
        phone: data.phoneNumber,
        fax: data.faxNumber,
      },
    },
  });
};

export const updateProject: UpdateProject = (projectId, data, isSameName) => {
  if (isSameName) {
    return axios({
      url: endpoint(`/projects/${projectId}`),
      method: 'PATCH',
      ...headers(),
      data: {
        addresses: {
          line1: data.line1,
          line2: data.line2,
          type: 'primary',
          province: data.province,
          district: data.district,
          subdistrict: data.subdistrict,
          postalCode: data.postalCode,
          telephone1: data.officeNumber,
        },
        contact: {
          phone: data.phoneNumber,
          fax: data.faxNumber,
        },
      },
    });
  } else {
    return axios({
      url: endpoint(`/projects/${projectId}`),
      method: 'PATCH',
      ...headers(),
      data: {
        name: data.projectName,
        addresses: {
          line1: data.line1,
          line2: data.line2,
          type: 'primary',
          province: data.province,
          district: data.district,
          subdistrict: data.subdistrict,
          postalCode: data.postalCode,
          telephone1: data.officeNumber,
        },
        contact: {
          phone: data.phoneNumber,
          fax: data.faxNumber,
        },
      },
    });
  }
};

export const deleteProject: DeleteProject = (projectId) => {
  return axios({
    url: endpoint(`/projects/${projectId}`),
    method: 'DELETE',
    ...headers(),
  });
};

export const listUsersInProject: ListUsersProject = ({
  projectId,
  offset,
  limit,
  firstName,
  lastName,
  email,
  phone,
  status,
  sync,
  roleName,
  groupName,
}) => {
  const isSync = sync ? (sync === 'completed' ? true : false) : undefined;
  return axios({
    url: endpoint(`/projects/${projectId}/users`),
    method: 'GET',
    params: {
      offset,
      limit,
      firstName,
      lastName,
      email,
      phone,
      userStatus: status,
      roleName,
      groupName,
      isSync: isSync ?? undefined,
    },
    ...headers(),
  });
};

export const listRoles: ListRolesProject = (projectId, offset, limit, brief) => {
  return axios({
    url: endpoint(`/projects/${projectId}/roles`),
    method: 'GET',
    params: {
      offset: offset,
      limit: limit ?? getRowsPerPage(),
      brief,
    },
    ...headers(),
  });
};

export const createRoleInProject: CreateRoleInProject = (projectId, roleName, permission) => {
  const statementData = permission.map((item: string) => ({
    permission: item,
    resources: ['*'],
  }));

  return axios({
    url: endpoint(`/projects/${projectId}/roles`),
    method: 'POST',
    ...headers(),
    data: {
      name: roleName,
      statements: statementData,
    },
  });
};

export const fetchRoleInProjectWithId: FetchRoleProjectWithId = (projectId, roleId) => {
  return axios({
    url: endpoint(`/projects/${projectId}/roles/${roleId}`),
    method: 'GET',
    ...headers(),
  });
};

export const updateRoleInProject: UpdateRoleInProject = (
  projectId,
  roleId,
  roleName,
  permission,
) => {
  const statementData = permission.map((item: string) => ({
    permission: item,
    resources: ['*'],
  }));

  return axios({
    url: endpoint(`/projects/${projectId}/roles/${roleId}`),
    method: 'PATCH',
    ...headers(),
    data: {
      name: roleName || undefined,
      statements: statementData,
    },
  });
};

export const deleteRolesInProject: DeleteRolesInProject = (projectId, roleId) => {
  return axios({
    url: endpoint(`/projects/${projectId}/roles/${roleId}`),
    method: 'DELETE',
    ...headers(),
  });
};

export const manageUsersInProject: ManageUserInProject = (projectId, userId, roleId, groupIds) => {
  console.log(groupIds || undefined);
  return axios({
    url: endpoint(`/projects/${projectId}/users`),
    method: 'PUT',
    ...headers(),
    data: {
      userId,
      roleId: roleId || undefined,
      groupIds: groupIds || undefined,
    },
  });
};

export const inviteMember: InviteMember = (projectId, { firstName, lastName, email, roleId }) => {
  return axios({
    url: endpoint(`/projects/${projectId}/users`),
    method: 'POST',
    ...headers(),
    data: {
      firstName,
      lastName,
      email: email || undefined,
      roleId,
    },
  });
};

export const deleteUsersInProject: DeleteUserInProject = (projectId, userId) => {
  return axios({
    url: endpoint(`/projects/${projectId}/remove-user`),
    method: 'PUT',
    ...headers(),
    data: {
      userId: userId,
    },
  });
};

export const fetchAllLockers: FetchAllLockers = (projectId, brief) => {
  return axios({
    url: endpoint('/lockers'),
    method: 'GET',
    ...headers(),
    params: {
      projectId,
      offset: 0,
      limit: 0,
      brief,
    },
  });
};

export const fetchAllDoors: FetchAllDoors = (projectId, brief) => {
  return axios({
    url: endpoint('/doors'),
    method: 'GET',
    ...headers(),
    params: {
      projectId,
      offset: 0,
      limit: 0,
      brief,
    },
  });
};

export const groupWithId: GroupWithId = (projectId, groupId) => {
  return axios({
    url: endpoint(`/projects/${projectId}/groups/${groupId}`),
    method: 'GET',
    ...headers(),
  });
};

export const listGroups: ListGroups = ({ projectId, offset, limit, name, types, brief }) => {
  const project = projectId || getProject();
  return axios({
    url: endpoint(`/projects/${project}/groups`),
    method: 'GET',
    ...headers(),
    params: {
      offset: offset,
      limit: limit,
      brief: brief,
      name: name || undefined,
      assetTypes: types || undefined,
    },
  });
};

export const fetchLockerById: LockerById = (lockerId) => {
  return axios({
    url: endpoint(`/lockers/${lockerId}`),
    method: 'GET',
    ...headers(),
  });
};

export const fetchLockerByIds: FetchLockersByIds = (Ids) => {
  return axios({
    url: endpoint(`/lockers`),
    method: 'GET',
    ...headers(),
    params: {
      ids: Ids,
      brief: true,
    },
  });
};

export const createGroup: CreateGroup = (
  projectId,
  groupName,
  lockerIds,
  doorIds,
  useableAssets,
) => {
  return axios({
    url: endpoint(`/projects/${projectId}/groups`),
    method: 'POST',
    ...headers(),
    data: {
      name: groupName,
      lockerIds: lockerIds,
      doorIds: doorIds,
      useableAssets: useableAssets || undefined,
    },
  });
};

export const updateGroup: UpdateGroup = (
  projectId,
  groupId,
  groupName,
  lockerIds,
  doorIds,
  useableAssets,
) => {
  return axios({
    url: endpoint(`/projects/${projectId}/groups/${groupId}`),
    method: 'PATCH',
    ...headers(),
    data: {
      name: groupName || undefined,
      lockerIds: lockerIds || undefined,
      doorIds: doorIds || undefined,
      useableAssets: useableAssets || undefined,
    },
  });
};

export const deleteGroup: DeleteGroup = (projectId, groupId) => {
  return axios({
    url: endpoint(`/projects/${projectId}/groups/${groupId}`),
    method: 'DELETE',
    ...headers(),
  });
};

export const listDevices: ListDevices = (params) => {
  const { projectId } = params;
  return axios({
    url: endpoint(`/devices`),
    method: 'GET',
    ...headers(),
    params: {
      projectId: projectId ? projectId : undefined,
    },
  });
};

export const updateCardGroup: UpdateCardGroup = (projectId, groupId, cardIds) => {
  return axios({
    url: endpoint(`/projects/${projectId}/groups/${groupId}`),
    method: 'PATCH',
    ...headers(),
    data: {
      cardIds: cardIds,
      useableAssets: cardIds,
    },
  });
};

export const uploadProjectPicture: UploadProjectPicture = (projectId, file) => {
  const formData = new FormData();
  formData.append('file', file);

  return axios({
    url: endpoint(`/projects/${projectId}/upload`),
    method: 'POST',
    data: formData,
    ...headers({ 'Content-Type': 'multipart/form-data' }),
  });
};
